import type { KeyboardEvent } from "react";

/**
 * Arrow/Home/End navigation for a `role="tablist"` made of ordinary buttons or
 * links (`role="tab"`). Works on either element type: moving focus is done
 * with a plain `.focus()` + `.click()`, which is all a `<button onClick>` or a
 * router `<NavLink>` needs to activate the next tab.
 *
 * Attach it to the tablist container, not to each tab: the design-system's
 * ProfileTabs renders the tabs itself and only forwards props to the
 * surrounding `<nav>`, so the handler has to work by delegation. `event.target`
 * is the tab the key was pressed on; `currentTarget` is whatever the handler
 * was attached to, which stays correct when it is attached to a tab directly.
 */
export function handleTabArrowKeyDown(event: KeyboardEvent<HTMLElement>) {
  const { key } = event;
  if (key !== "ArrowRight" && key !== "ArrowLeft" && key !== "Home" && key !== "End") {
    return;
  }

  const current = event.target as HTMLElement | null;
  if (!current?.closest) {
    return;
  }

  const container = current.closest('[role="tablist"]');
  if (!container) {
    return;
  }

  const tabs = Array.from(container.querySelectorAll<HTMLElement>('[role="tab"]'));
  const currentIndex = tabs.indexOf(current);
  if (currentIndex === -1) {
    return;
  }

  let nextIndex = currentIndex;
  if (key === "ArrowRight") nextIndex = (currentIndex + 1) % tabs.length;
  if (key === "ArrowLeft") nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
  if (key === "Home") nextIndex = 0;
  if (key === "End") nextIndex = tabs.length - 1;

  if (nextIndex === currentIndex) {
    return;
  }

  event.preventDefault();
  const next = tabs[nextIndex];
  next.focus();
  next.click();
}
